import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { LOCALE_ID, NgModule, inject, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AuthModule } from 'angular-auth-oidc-client';

import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { OrdersDialogsHostComponent } from '@app/orders/components';
import { AbstractPrintService } from '@core/services/printing/abstract-print.service';
import { SharedPrintTicketService } from '@core/services/printing/print-ticket.service';
import { RumService } from '@core/services/rum.service';
import { environment } from '@environments/environment';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { SharedModule } from '@shared/shared.module';
import config from 'devextreme/core/config';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { Constants } from './core/app-constants';
import { SessionExpirationService } from './core/auth/session-expiration.service';
import { CoreModule } from './core/core.module';
import { CustomTranslateLoaderService } from './core/services/custom-translate-loader.service';
import { RootStoreModule } from './root-store/root-store.module';
import { FullscreenOverlayContainer, OVERLAY_DEFAULT_CONFIG, OverlayContainer } from '@angular/cdk/overlay';
registerLocaleData(localeFr);

config({ licenseKey: environment.devExtreme.licenseKey });

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

const authConfig = {
  configId: Constants.AuthConfigId,
  authority: environment.Authentication.authority,
  clientId: environment.Authentication.clientId,
  scope: environment.Authentication.scope,
  responseType: 'code',
  redirectUrl: `${window.location.origin}/auth/callback`,
  postLogoutRedirectUri: window.location.origin,
  postLoginRoute: '/auth/callback',
  forbiddenRoute: '/auth/forbidden',
  logLevel: environment.Authentication.logLevel,
  silentRenew: true,
  silentRenewUrl: `${window.location.origin}/silent-renew.html`,
  secureRoutes: [environment.API_BASE_URL],
  renewTimeBeforeTokenExpiresInSeconds: 60,
  useRefreshToken: false,
  autoUserInfo: true,
  renewUserInfoAfterTokenRenew: true,
  maxIdTokenIatOffsetAllowedInSeconds: 600,
};
@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    CoreModule.forRoot(),
    AuthModule.forRoot({ config: authConfig }),
    BrowserAnimationsModule,
    MatSidenavModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    TranslateModule.forRoot({
      loader: { provide: TranslateLoader, useClass: CustomTranslateLoaderService, deps: [HttpClient] },
    }),
    RootStoreModule,
    NgIdleKeepaliveModule.forRoot(),
    SharedModule,
    OrdersDialogsHostComponent,
  ],
  providers: [
    { provide: LOCALE_ID, useValue: 'fr-FR' },
    { provide: AbstractPrintService, useClass: SharedPrintTicketService },
   { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    // Doit rester UNIQUEMENT ici (injecteur racine) : chaque injecteur qui fournit OverlayContainer
    // instancie son propre `<div class="cdk-overlay-container">`. Comme tous ces conteneurs partagent
    // le meme z-index 9999 (reset-material.scss), leur empilement depend de l'ordre de creation
    // (donc de la navigation de l'utilisateur) => z-index aleatoire. Un seul conteneur = empilement
    // deterministe par ordre du DOM, comportement attendu par le CDK.
    { provide: OverlayContainer, useClass: FullscreenOverlayContainer },
    RumService,
    provideHttpClient(withInterceptorsFromDi()),
    // GEN-7589 : la surveillance doit etre armee avant la premiere navigation, pour qu'une
    // perte de session survenant pendant le demarrage ne passe pas inapercue.
    provideAppInitializer(() => inject(SessionExpirationService).start()),
  ],
})
export class AppModule {}
